import React, { useReducer, useState, useEffect, useMemo } from "react";
import {
  luckyDrawReducer,
  initialLuckyDrawState,
  canDraw,
  performDraw,
  getRemainingCount,
  DrawMode,
} from "./LuckyDrawReducer";
import ProductGameHeader from "../ProductGameHeader";
import { useTranslation } from "react-i18next";
import { Product } from "../../module/types";
import { useNavigate } from "react-router-dom";
import SEO from "../../components/SEO";

interface Props {
  product: Product;
}

const LuckyDrawView: React.FC<Props> = ({ product }) => {
  const { t, i18n } = useTranslation();
  const [state, dispatch] = useReducer(luckyDrawReducer, initialLuckyDrawState);
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [newPrizeName, setNewPrizeName] = useState("");
  const [newPrizeQuantity, setNewPrizeQuantity] = useState(1);
  const [animationText, setAnimationText] = useState("");
  const navigate = useNavigate();

  const {
    mode,
    minNumber,
    maxNumber,
    prizes,
    started,
    isDrawing,
    drawHistory,
    lastDraw,
    drawnNumbers,
  } = state;

  // 사용 가능한 숫자 목록을 미리 계산 (메모리 최적화)
  const availableNumbers = useMemo(() => {
    if (mode !== "number") return [];
    return Array.from(
      { length: maxNumber - minNumber + 1 },
      (_, i) => i + minNumber
    ).filter((num) => !drawnNumbers.includes(num));
  }, [mode, minNumber, maxNumber, drawnNumbers]);

  // 사용 가능한 경품 목록을 미리 계산 (메모리 최적화)
  const availablePrizes = useMemo(() => {
    if (mode !== "prize") return [];
    return prizes.filter((p) => p.remaining > 0);
  }, [mode, prizes]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isDrawing) {
      interval = setInterval(() => {
        if (mode === "number") {
          // 미리 계산된 사용 가능한 숫자 목록 사용
          if (availableNumbers.length > 0) {
            const randomIndex = Math.floor(
              Math.random() * availableNumbers.length
            );
            setAnimationText(availableNumbers[randomIndex].toString());
          }
        } else {
          // 미리 계산된 사용 가능한 경품 목록 사용
          if (availablePrizes.length > 0) {
            const randomIndex = Math.floor(
              Math.random() * availablePrizes.length
            );
            setAnimationText(availablePrizes[randomIndex].name);
          }
        }
      }, 80);
    }

    return () => clearInterval(interval);
  }, [isDrawing, mode, availableNumbers, availablePrizes]);

  const handleModeChange = (newMode: DrawMode) => {
    dispatch({ type: "SET_MODE", mode: newMode });
  };

  const handleMinNumberChange = (newNumber: number) => {
    dispatch({ type: "SET_MIN_NUMBER", number: newNumber });
  };

  const handleMaxNumberChange = (newNumber: number) => {
    dispatch({ type: "SET_MAX_NUMBER", number: newNumber });
  };

  const handleBackToSettings = () => {
    dispatch({ type: "BACK_TO_SETTINGS" });
  };

  const handleAddPrize = () => {
    if (newPrizeName.trim()) {
      dispatch({
        type: "ADD_PRIZE",
        name: newPrizeName.trim(),
        quantity: newPrizeQuantity,
      });
      setNewPrizeName("");
      setNewPrizeQuantity(1);
    }
  };

  const handleDeletePrize = (id: string) => {
    dispatch({ type: "DELETE_PRIZE", id });
  };

  const handleStartDraw = () => {
    if (!started) {
      dispatch({ type: "START_DRAW" });
    } else {
      if (isDrawing) {
        // 애니메이션 중이면 중지하고 결과 확정
        const result = performDraw(state);
        if (result) {
          dispatch({ type: "COMPLETE_DRAW", result });
          setAnimationText(result);
        }
      } else if (canDraw(state)) {
        // 애니메이션 시작
        dispatch({ type: "START_DRAWING" });
      }
    }
  };

  const handleReset = () => {
    if (mode === "number") {
      dispatch({ type: "RESET_NUMBERS" });
    } else {
      dispatch({ type: "RESET_PRIZES" });
    }
  };

  const handleLeaveRoom = () => {
    navigate("/", { replace: true });
  };

  return (
    <>
      <SEO
        title={`${product.title} - Lingple`}
        description={
          i18n.language === "ko"
            ? "숫자와 경품 추첨을 도와드립니다."
            : "Helps you conduct number and prize draws."
        }
        keywords={
          i18n.language === "ko"
            ? "숫자 추첨, 경품 추첨, 랜덤 선택, 행사"
            : "number draw, prize draw, random selection, event"
        }
      />
      <div className="space-gray-theme lucky-draw-container flex flex-col h-screen bg-gray-900">
        <ProductGameHeader
          product={product}
          isRuleOpen={isRuleOpen}
          setIsRuleOpen={setIsRuleOpen}
          isSettingOpen={isSettingOpen}
          setIsSettingOpen={setIsSettingOpen}
          t={t}
        />

        {/* Setting 메뉴 */}
        {isSettingOpen && (
          <div className="fixed right-0 top-[48px] mt-2 w-40 bg-gray-800 rounded-lg shadow-xl z-40 border border-gray-700">
            <ul className="py-1">
              <li
                className="px-4 py-2 hover:bg-gray-700 cursor-pointer text-red-400 hover:text-white transition-colors"
                onClick={handleLeaveRoom}
              >
                <i className="fas fa-sign-out-alt mr-2" />
                {t("leaveRoom")}
              </li>
            </ul>
          </div>
        )}

        <div className="lucky-draw-main flex-1 flex flex-col min-h-0 pt-[48px] overflow-y-auto">
          <div className="flex-1 flex flex-col items-center justify-start pt-4 px-4">
            {/* 모드 선택 - 설정 화면에서만 표시 */}
            {!started && (
              <div className="flex justify-center mb-6">
                <div className="bg-gray-800 p-1 rounded-lg border border-gray-600">
                  <button
                    onClick={() => handleModeChange("number")}
                    className={`px-6 py-2 rounded-md font-medium transition-colors ${
                      mode === "number"
                        ? "bg-blue-600 text-white"
                        : "text-gray-400 hover:text-white"
                    }`}
                  >
                    <i className="fas fa-hashtag mr-2" />
                    {t("LuckyDraw.numberMode")}
                  </button>
                  <button
                    onClick={() => handleModeChange("prize")}
                    className={`px-6 py-2 rounded-md font-medium transition-colors ${
                      mode === "prize"
                        ? "bg-purple-600 text-white"
                        : "text-gray-400 hover:text-white"
                    }`}
                  >
                    <i className="fas fa-gift mr-2" />
                    {t("LuckyDraw.prizeMode")}
                  </button>
                </div>
              </div>
            )}

            {started ? (
              /* 추첨 영역 */
              <div className="max-w-4xl w-full mx-auto p-4">
                <div className="text-center mt-4 mb-12">
                  <div
                    className={`text-8xl font-bold h-32 flex items-center justify-center ${
                      isDrawing ? "animate-pulse" : ""
                    }`}
                  >
                    {isDrawing ? (
                      <span className="text-green-400">{animationText}</span>
                    ) : lastDraw ? (
                      <span
                        className={
                          mode === "number"
                            ? "text-yellow-400"
                            : "text-purple-400"
                        }
                      >
                        {lastDraw.result}
                      </span>
                    ) : (
                      <span className="text-gray-500">?</span>
                    )}
                  </div>
                </div>

                <div className="text-center mb-12">
                  <button
                    onClick={handleStartDraw}
                    className={`w-64 py-6 pl-6 text-white rounded-xl font-bold text-2xl transition-colors relative mx-auto ${
                      canDraw(state) || isDrawing
                        ? isDrawing
                          ? "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                          : mode === "number"
                          ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                          : "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
                        : "bg-gray-600 cursor-not-allowed"
                    }`}
                  >
                    <i className="fas fa-dice text-2xl absolute left-10 top-1/2 transform -translate-y-1/2" />
                    {isDrawing
                      ? t("LuckyDraw.stopDrawing")
                      : canDraw(state)
                      ? t("LuckyDraw.startDrawing")
                      : t("LuckyDraw.drawingComplete")}
                  </button>

                  <div className="text-gray-400 mt-4 text-lg">
                    {mode === "number"
                      ? t("LuckyDraw.remainingNumbers")
                      : t("LuckyDraw.remainingPrizes")}
                    : {getRemainingCount(state)}
                    {t("LuckyDraw.units")}
                  </div>
                </div>

                {drawHistory.length > 0 && (
                  <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-600">
                    <h4 className="text-2xl font-bold text-white mb-6 flex items-center">
                      <i className="fas fa-history text-yellow-400 mr-3" />
                      {t("LuckyDraw.drawHistory")}
                    </h4>

                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                      {drawHistory.slice(0, 24).map((draw) => (
                        <div
                          key={draw.id}
                          className={`p-4 rounded-lg text-center font-medium text-base ${
                            mode === "number"
                              ? "bg-blue-900/30 border border-blue-700/30 text-blue-200"
                              : "bg-purple-900/30 border border-purple-700/30 text-purple-200"
                          }`}
                        >
                          {draw.result.length > 10
                            ? `${draw.result.substring(0, 10)}...`
                            : draw.result}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-center mt-20">
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={handleReset}
                      className="w-48 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-medium text-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-redo text-base" />
                      {t("LuckyDraw.restart")}
                    </button>
                    <button
                      onClick={handleBackToSettings}
                      className="w-48 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-medium text-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-times text-base" />
                      {t("LuckyDraw.backToSettings")}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* 설정 영역 */
              <>
                {mode === "number" && (
                  <div className="max-w-2xl w-full mx-auto bg-gray-800/50 rounded-xl p-8 border border-gray-600">
                    <h3 className="text-2xl font-bold text-white mb-8 text-center flex items-center justify-center">
                      <i className="fas fa-hashtag text-blue-400 mr-3" />
                      {t("LuckyDraw.numberModeSettings")}
                    </h3>

                    <div className="space-y-4 mb-8">
                      {/* 최소 숫자 설정 */}
                      <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border border-green-700/30 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-3">
                          <label className="text-base font-medium text-green-300 whitespace-nowrap">
                            {t("LuckyDraw.minimumNumber")}
                          </label>
                          <div className="flex items-center justify-center gap-2 ml-auto">
                            <button
                              onClick={() =>
                                handleMinNumberChange(minNumber - 1)
                              }
                              disabled={minNumber <= 1}
                              className="px-3 py-2 text-green-300 hover:text-white hover:bg-green-600/20 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors text-base"
                            >
                              −
                            </button>
                            <input
                              type="number"
                              value={minNumber}
                              onChange={(e) =>
                                handleMinNumberChange(
                                  Math.max(
                                    1,
                                    Math.min(
                                      maxNumber - 1,
                                      parseInt(e.target.value) || 1
                                    )
                                  )
                                )
                              }
                              className="w-16 text-center text-white font-medium text-base bg-gray-800/50 border border-green-500 rounded py-2 focus:border-green-400 focus:outline-none appearance-none"
                            />
                            <button
                              onClick={() =>
                                handleMinNumberChange(minNumber + 1)
                              }
                              disabled={minNumber >= maxNumber - 1}
                              className="px-3 py-2 text-green-300 hover:text-white hover:bg-green-600/20 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors text-base"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* 최대 숫자 설정 */}
                      <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border border-blue-700/30 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-3">
                          <label className="text-base font-medium text-blue-300 whitespace-nowrap">
                            {t("LuckyDraw.maximumNumber")}
                          </label>
                          <div className="flex items-center justify-center gap-2 ml-auto">
                            <button
                              onClick={() =>
                                handleMaxNumberChange(maxNumber - 1)
                              }
                              disabled={maxNumber <= minNumber + 1}
                              className="px-3 py-2 text-blue-300 hover:text-white hover:bg-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors text-base"
                            >
                              −
                            </button>
                            <input
                              type="number"
                              value={maxNumber}
                              onChange={(e) =>
                                handleMaxNumberChange(
                                  Math.max(
                                    minNumber + 1,
                                    Math.min(
                                      9999,
                                      parseInt(e.target.value) || minNumber + 1
                                    )
                                  )
                                )
                              }
                              className="w-16 text-center text-white font-medium text-base bg-gray-800/50 border border-blue-500 rounded py-2 focus:border-blue-400 focus:outline-none appearance-none"
                            />
                            <button
                              onClick={() =>
                                handleMaxNumberChange(maxNumber + 1)
                              }
                              disabled={maxNumber >= 9999}
                              className="px-3 py-2 text-blue-300 hover:text-white hover:bg-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors text-base"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* 범위 표시 */}
                      <div className="text-center text-gray-400 text-base">
                        {t("LuckyDraw.drawFromRange", { minNumber, maxNumber })}
                      </div>
                    </div>

                    <div className="text-center">
                      <button
                        onClick={handleStartDraw}
                        disabled={maxNumber <= minNumber}
                        className="px-10 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-xl font-bold text-xl transition-colors flex items-center gap-3 mx-auto"
                      >
                        <i className="fas fa-play text-lg" />
                        {t("LuckyDraw.startDraw")}
                      </button>
                      {maxNumber <= minNumber && (
                        <div className="text-gray-400 text-base mt-3">
                          {t("LuckyDraw.maxNumberMustBeGreater")}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {mode === "prize" && (
                  <div className="max-w-4xl w-full mx-auto bg-gray-800/50 rounded-xl p-8 border border-gray-600">
                    <h3 className="text-2xl font-bold text-white mb-8 text-center flex items-center justify-center">
                      <i className="fas fa-gift text-purple-400 mr-3" />
                      {t("LuckyDraw.prizeModeSettings")}
                    </h3>

                    {/* 경품 추가 영역 */}
                    <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-700/30 rounded-lg px-4 py-3 mb-8">
                      <div className="flex flex-col gap-3">
                        {/* 경품명 입력 */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-purple-300 whitespace-nowrap w-16">
                            {t("LuckyDraw.prizeName")}
                          </span>
                          <input
                            type="text"
                            placeholder={t("LuckyDraw.enterPrizeName")}
                            value={newPrizeName}
                            onChange={(e) => setNewPrizeName(e.target.value)}
                            onKeyPress={(e) =>
                              e.key === "Enter" && handleAddPrize()
                            }
                            className="flex-1 min-w-0 px-2 py-1 bg-gray-800/50 rounded text-white placeholder-gray-500/50 border-purple-500 ring-1 ring-purple-500/30 text-sm"
                          />
                        </div>

                        {/* 수량 조절과 추가 버튼 */}
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2 flex-1">
                            <div className="text-purple-300 text-sm font-medium whitespace-nowrap w-16">
                              {t("LuckyDraw.quantity")}
                            </div>
                            <div className="flex items-center bg-gray-800/50 rounded border-purple-500 ring-1 ring-purple-500/30">
                              <button
                                onClick={() =>
                                  setNewPrizeQuantity(
                                    Math.max(1, newPrizeQuantity - 1)
                                  )
                                }
                                disabled={newPrizeQuantity <= 1}
                                className="px-2 py-1 text-purple-300 hover:text-white hover:bg-purple-600/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-l transition-colors text-sm"
                              >
                                −
                              </button>
                              <span className="w-12 px-1 py-1 text-center text-white text-sm">
                                {newPrizeQuantity}
                              </span>
                              <button
                                onClick={() =>
                                  setNewPrizeQuantity(newPrizeQuantity + 1)
                                }
                                className="px-2 py-1 text-purple-300 hover:text-white hover:bg-purple-600/20 rounded-r transition-colors text-sm"
                              >
                                +
                              </button>
                            </div>
                          </div>
                          <button
                            onClick={handleAddPrize}
                            disabled={!newPrizeName.trim()}
                            className="flex-1 py-1 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors text-sm flex items-center justify-center gap-2"
                          >
                            <i className="fas fa-plus-circle text-sm" />
                            <span>{t("LuckyDraw.add")}</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {prizes.length > 0 && (
                      <div className="mb-10">
                        <h4 className="text-xl font-medium text-white mb-6">
                          {t("LuckyDraw.registeredPrizes")}
                        </h4>
                        <div className="space-y-3 max-h-48 overflow-y-auto">
                          {prizes.map((prize) => (
                            <div
                              key={prize.id}
                              className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-600"
                            >
                              <div className="flex-1">
                                <span className="text-white font-medium text-lg">
                                  {prize.name}
                                </span>
                                <span className="text-gray-400 ml-3 text-base">
                                  × {prize.quantity}
                                  {t("LuckyDraw.units")}
                                </span>
                              </div>
                              <button
                                onClick={() => handleDeletePrize(prize.id)}
                                className="w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center"
                              >
                                <i className="fas fa-trash text-base" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="text-center">
                      <button
                        onClick={handleStartDraw}
                        disabled={prizes.length === 0}
                        className="px-10 py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-xl font-bold text-xl transition-colors flex items-center gap-3 mx-auto"
                      >
                        <i className="fas fa-play text-lg" />
                        {t("LuckyDraw.startDraw")}
                      </button>
                      {prizes.length === 0 && (
                        <div className="text-gray-400 text-base mt-3">
                          {t("LuckyDraw.addPrizesFirst")}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LuckyDrawView;
